import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Clock, ArrowRight, AlertCircle, Copy, Check } from 'lucide-react';
import { Link } from 'wouter';
import { fetchLatestBlocks, type BlockData } from '@/data-access/blocks';

interface LatestBlocksTableProps {
  className?: string;
}

const defaultBlocks: BlockData[] = [];

export function LatestBlocksTable({ className }: LatestBlocksTableProps) {
  const [copiedHeight, setCopiedHeight] = useState<number | null>(null);
  
  const { 
    data: blocks, 
    isLoading, 
    error 
  } = useQuery({
    queryKey: ['latest-blocks'],
    queryFn: fetchLatestBlocks,
    refetchInterval: 30000, // Refetch every 30 seconds
    staleTime: 15000, // Consider data stale after 15 seconds
  });

  const handleCopyHeight = (height: number) => {
    navigator.clipboard.writeText(height.toString());
    setCopiedHeight(height);
    setTimeout(() => setCopiedHeight(null), 2000);
  };

  // Use real data if available, otherwise fall back to default
  const displayBlocks = blocks || defaultBlocks;
  return (
    <Card className={className}>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
        <div className="flex items-center space-x-3">
          <div className="p-2 rounded-full bg-blue-100 dark:bg-blue-900/20">
            <div className="w-6 h-6 flex items-center justify-center">
              <div className="grid grid-cols-2 gap-0.5">
                <div className="w-2 h-2 bg-blue-600 dark:bg-blue-400 rounded-sm"></div>
                <div className="w-2 h-2 bg-blue-600 dark:bg-blue-400 rounded-sm"></div>
                <div className="w-2 h-2 bg-blue-600 dark:bg-blue-400 rounded-sm"></div>
                <div className="w-2 h-2 bg-blue-600 dark:bg-blue-400 rounded-sm"></div>
              </div>
            </div>
          </div>
          <div>
            <div className="flex items-center space-x-2">
              <h3 className="text-lg font-semibold text-foreground">Latest Blocks</h3>
              {error && (
                <div className="flex items-center space-x-1">
                  <div className="w-2 h-2 bg-yellow-500 rounded-full"></div>
                  <span className="text-xs text-muted-foreground">Cached</span>
                </div>
              )}
              {!isLoading && !error && (
                <div className="flex items-center space-x-1">
                  <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                  <span className="text-xs text-muted-foreground">Live</span>
                </div>
              )}
            </div>
            <p className="text-sm text-muted-foreground">Recently mined network blocks</p>
          </div>
        </div>
      </CardHeader>
      <CardContent className="pt-0">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="font-semibold text-muted-foreground uppercase text-xs tracking-wider">
                  Block Height
                </TableHead>
                <TableHead className="font-semibold text-muted-foreground uppercase text-xs tracking-wider">
                  Time Mined (UTC)
                </TableHead>
                <TableHead className="font-semibold text-muted-foreground uppercase text-xs tracking-wider text-right">
                  Tx Count
                </TableHead>
                <TableHead className="font-semibold text-muted-foreground uppercase text-xs tracking-wider text-right">
                  Total Fees (BTC)
                </TableHead>
                <TableHead className="font-semibold text-muted-foreground uppercase text-xs tracking-wider text-right">
                  Avg Fee (sat/vB)
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                // Loading skeleton rows
                Array.from({ length: 5 }).map((_, index) => (
                  <TableRow key={`skeleton-${index}`}>
                    <TableCell><Skeleton className="h-4 w-20" /></TableCell>
                    <TableCell><Skeleton className="h-4 w-16" /></TableCell>
                    <TableCell><Skeleton className="h-4 w-16" /></TableCell>
                    <TableCell><Skeleton className="h-4 w-16" /></TableCell>
                    <TableCell><Skeleton className="h-4 w-16" /></TableCell>
                  </TableRow>
                ))
              ) : (
                // Data rows
                displayBlocks.map((block) => (
                  <TableRow key={block.height} className="hover:bg-muted/50">
                    <TableCell>
                      <div className="flex items-center space-x-2">
                        <Link 
                          href={`/search/block-height?query=${block.height}`} 
                          className="text-blue-600 dark:text-blue-400 font-mono font-medium hover:text-blue-700 dark:hover:text-blue-300 transition-colors cursor-pointer"
                        >
                          #{block.height}
                        </Link>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-6 w-6 p-0 text-muted-foreground hover:text-blue-600 dark:hover:text-blue-400"
                          onClick={() => handleCopyHeight(block.height)}
                        >
                          {copiedHeight === block.height ? (
                            <Check className="w-3 h-3" />
                          ) : (
                            <Copy className="w-3 h-3" />
                          )}
                        </Button>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center space-x-1">
                        <Clock className="w-4 h-4 text-muted-foreground" />
                        <span className="text-sm">{block.timestamp}</span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="text-right">
                        <div className="font-medium">{block.transactions.toLocaleString()}</div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="text-right flex items-center justify-end gap-1">
                        <div className="font-medium font-mono">{(block.totalFees / 100000000).toFixed(8)}</div>
                        <div className="text-xs text-muted-foreground">BTC</div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="text-right flex items-center justify-end gap-1">
                        <div className="font-medium font-mono">~{block.avgFeePerVByte}</div>
                        <div className="text-xs text-muted-foreground ml-1">sat/vB</div>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </CardContent>
    </Card>
  );
}